import { useEffect, useRef, useState } from 'react';
import { requestPairingCode } from '@/services/api/transfer';

type State =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'shown'; code: string; expiresAt: number }
  | { kind: 'error'; message: string };

export function ReceiveTransferButton() {
  const [state, setState] = useState<State>({ kind: 'idle' });
  const [remainingSec, setRemainingSec] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (state.kind !== 'shown') return;
    const tick = () => {
      const remaining = Math.max(
        0,
        Math.floor((state.expiresAt - Date.now()) / 1000),
      );
      setRemainingSec(remaining);
      if (remaining === 0) {
        setState({ kind: 'idle' });
      }
    };
    tick();
    timerRef.current = setInterval(tick, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [state]);

  async function onRequest() {
    setState({ kind: 'loading' });
    try {
      const { code, expiresAt } = await requestPairingCode();
      const expiresAtMs = Date.parse(expiresAt);
      setState({
        kind: 'shown',
        code,
        expiresAt: Number.isFinite(expiresAtMs)
          ? expiresAtMs
          : Date.now() + 5 * 60 * 1000,
      });
    } catch {
      setState({ kind: 'error', message: 'Impossible de générer un code.' });
    }
  }

  if (state.kind === 'shown') {
    const mm = Math.floor(remainingSec / 60);
    const ss = String(remainingSec % 60).padStart(2, '0');
    return (
      <div className="bg-brand-dark/60 rounded-2xl p-4 flex flex-col items-center gap-2 shadow-card">
        <p className="text-xs text-white/70 uppercase tracking-wide">
          Donnez ce code à votre collègue
        </p>
        <p className="text-4xl font-bold tracking-[0.25em]">{state.code}</p>
        <p className="text-xs text-white/60">
          Expire dans {mm}:{ss}
        </p>
        <button
          type="button"
          className="text-xs text-white/60 underline mt-1"
          onClick={() => setState({ kind: 'idle' })}
        >
          Fermer
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-1">
      <button
        type="button"
        onClick={() => void onRequest()}
        disabled={state.kind === 'loading'}
        className="rounded-pill bg-white/10 text-white text-sm font-semibold px-4 py-2 disabled:opacity-50"
      >
        {state.kind === 'loading'
          ? 'Génération du code…'
          : 'Recevoir un transfert'}
      </button>
      {state.kind === 'error' && (
        <p className="text-xs text-red-300">{state.message}</p>
      )}
    </div>
  );
}
